// Everything about one document, in a panel over the right of the page: review, who it's for,
// how answers read it, and who can open it.

import { useEffect, useState } from "react";
import { INCLUSION_HINTS, INCLUSION_LABELS, REVIEW_LABELS, inclusionOf, type Inclusion, type Page } from "@ask-hr/core/documents";
import { APPLICABILITY_LABELS, AUTO_APPLICABILITY_BY, type ApplicabilityValue, type SharePointSource } from "@ask-hr/core/sources";
import { api } from "../api.js";
import { Drawer } from "../components/Drawer.js";
import { ErrorNotice } from "../components/ErrorNotice.js";
import { ApplicabilityOptions } from "./ApplicabilityOptions.js";
import { ADMIN_ONLY, INCLUSIONS, PILL, day, errorText, explainDocumentsError, folderInSource, k, type Doc } from "./documents.js";
import { label } from "./sources.js";
import { VisibilityDetail } from "./Visibility.js";

export function DocumentPanel({
  doc: d,
  src,
  isAdmin,
  busy,
  sameAudience,
  onClose,
  onShowGroup,
  act,
}: {
  doc: Doc;
  src: SharePointSource | undefined;
  isAdmin: boolean;
  busy: string | null;
  sameAudience: boolean;
  onClose: () => void;
  onShowGroup: (group: string) => void;
  act: (fn: () => Promise<unknown>, done: string) => void;
}) {
  const url = `/api/admin/documents/${encodeURIComponent(d.id)}`;
  const later = d.approved ? " Publish to make it live." : "";
  const editable = Boolean(src) && d.status !== "removed";
  return (
    <Drawer
      title={d.name}
      subtitle={
        <>
          {src ? label(src) : d.driveName}
          {folderInSource(d, src)} · modified {day(d.modified)} ·{" "}
          <a href={d.webUrl} target="_blank" rel="noreferrer">
            Open in SharePoint
          </a>
        </>
      }
      onClose={onClose}
    >
      <section>
        <h4>Review</h4>
        <p>
          <span className={PILL[d.status]}>{REVIEW_LABELS[d.status]}</span>
          {d.status === "error" ? <span className="small"> {errorText(d.error)}</span> : null}
        </p>
        {d.current && d.status !== "removed" ? (
          <Review
            doc={d}
            busy={busy}
            onApprove={() => act(() => api("POST", `${url}/approve`, { hash: d.current?.hash }), `Approved ${d.name}. Publish to make it live.`)}
            onExclude={(exclude) =>
              act(() => api("POST", `${url}/${exclude ? "exclude" : "include"}`), exclude ? `Excluded ${d.name}.` : `${d.name} is back for review.`)
            }
          />
        ) : null}
      </section>

      <section>
        <h4>Who it's for</h4>
        <label className="field">
          <span>Applies to</span>
          {editable && src ? (
            <select
              value={d.applicability ?? ""}
              disabled={busy !== null}
              onChange={(e) => {
                const value = (e.target.value || null) as ApplicabilityValue | null;
                act(
                  () => api("PATCH", url, { applicability: value }),
                  `${d.name} now applies to: ${APPLICABILITY_LABELS[value ?? src.applicability]}.${later}`,
                );
              }}
            >
              <option value="">Same as source ({APPLICABILITY_LABELS[src.applicability]})</option>
              <ApplicabilityOptions />
            </select>
          ) : (
            <span>{src ? APPLICABILITY_LABELS[d.applicability ?? src.applicability] : "Source removed"}</span>
          )}
        </label>
        {d.applicability && d.applicabilityBy?.startsWith(AUTO_APPLICABILITY_BY) ? (
          <p className="hint">Set by {d.applicabilityBy}. Change it if that's wrong.</p>
        ) : null}
        <div className="field">
          <span>Variant group</span>
          {d.variantGroup ? (
            <span>
              {d.variantGroup}{" "}
              <button type="button" className="linkbtn" onClick={() => onShowGroup(d.variantGroup ?? "")}>
                Show the group
              </button>
              {sameAudience ? <span className="small muted"> · every version applies to the same people</span> : null}
            </span>
          ) : (
            <span className="muted small">None. To group versions, select them in the list and choose Group as location variants.</span>
          )}
        </div>
      </section>

      <section>
        <h4>How answers read it</h4>
        <label className="field">
          <span>Coverage</span>
          {isAdmin && d.status !== "removed" ? (
            <select
              value={inclusionOf(d)}
              disabled={busy !== null}
              onChange={(e) => {
                const value = e.target.value as Inclusion;
                act(() => api("PATCH", url, { inclusion: value }), `Coverage of ${d.name} is now ${INCLUSION_LABELS[value]}.${later}`);
              }}
            >
              {INCLUSIONS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          ) : (
            <span title={isAdmin ? undefined : ADMIN_ONLY}>{INCLUSION_LABELS[inclusionOf(d)]}</span>
          )}
        </label>
        <p className="hint">
          {INCLUSION_HINTS[inclusionOf(d)]}
          {d.current ? ` About ${k(d.current.tokens)} tokens.` : ""}
          {isAdmin ? "" : ` ${ADMIN_ONLY}`}
        </p>
      </section>

      <section>
        <h4>Who can open it</h4>
        {d.visibility ? (
          <VisibilityDetail
            v={d.visibility}
            webUrl={d.webUrl}
            intended={d.visibilityIntendedAt ? { by: d.visibilityIntendedBy ?? "", at: d.visibilityIntendedAt } : undefined}
            busy={busy !== null}
            onIntended={(intended) =>
              act(
                () => api("PATCH", url, { visibilityIntended: intended }),
                intended ? `${d.name} is marked as intended: it stays out of Needs attention.` : `${d.name} is no longer marked as intended.`,
              )
            }
          />
        ) : (
          <p className="sub">Not checked yet. The next sync checks it.</p>
        )}
      </section>
    </Drawer>
  );
}

function Review({
  doc,
  busy,
  onApprove,
  onExclude,
}: {
  doc: Doc;
  busy: string | null;
  onApprove: () => void;
  onExclude: (exclude: boolean) => void;
}) {
  const [pages, setPages] = useState<Page[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const hash = doc.current?.hash;

  useEffect(() => {
    let live = true;
    api<{ pages: Page[] }>("GET", `/api/admin/documents/${encodeURIComponent(doc.id)}/text`).then(
      (r) => live && setPages(r.pages),
      (e: unknown) => live && setError(e),
    );
    return () => {
      live = false;
    };
  }, [doc.id, hash]);

  const reviewing = doc.status === "new" || doc.status === "changed";
  return (
    <div className="review">
      <p className="sub">
        {doc.status === "changed"
          ? `A new version from ${day(doc.current?.modified)}. Ask HR keeps using the version approved ${day(doc.approvedAt)} by ${doc.approvedBy} until you approve this one and publish.`
          : doc.status === "new"
            ? "This is the text Ask HR will read. Check it came through correctly and belongs in answers."
            : doc.status === "approved"
              ? `Approved ${day(doc.approvedAt)} by ${doc.approvedBy}.`
              : doc.status === "excluded"
                ? `Excluded ${day(doc.excludedAt)} by ${doc.excludedBy}. Ask HR won't use it.`
                : null}
        {doc.current ? ` ${doc.current.pages} ${doc.current.pages === 1 ? "section" : "sections"}.` : ""}
      </p>
      {doc.current?.readFrom === "images" ? (
        <p className="notice">
          This is a scanned PDF, so Ask HR read its text from the page images. Check names, numbers and dates against{" "}
          <a href={doc.webUrl} target="_blank" rel="noreferrer">
            the PDF
          </a>{" "}
          before approving.
        </p>
      ) : null}
      {error ? <ErrorNotice error={error} explain={explainDocumentsError} /> : null}
      {pages === null && !error ? <p className="sub">Loading text…</p> : null}
      {pages ? (
        <div className="extract" tabIndex={0} aria-label={`Text of ${doc.name}`}>
          {pages.map((p) => (
            <section key={p.n}>
              <h4>{p.label}</h4>
              <p>{p.text}</p>
            </section>
          ))}
        </div>
      ) : null}
      <div className="actions start">
        {reviewing ? (
          <button type="button" className="primary" disabled={busy !== null || !pages} onClick={onApprove}>
            Approve this version
          </button>
        ) : null}
        {doc.status === "excluded" ? (
          <button type="button" disabled={busy !== null} onClick={() => onExclude(false)}>
            Include again
          </button>
        ) : (
          <button type="button" disabled={busy !== null} onClick={() => onExclude(true)}>
            Exclude from Ask HR
          </button>
        )}
      </div>
    </div>
  );
}
